/**
 * Ô chọn địa bàn Tỉnh → Xã.
 *
 * Cây địa bàn chỉ có HAI cấp: cải cách hành chính 2025 đã bỏ cấp huyện, và
 * nguồn dữ liệu thật (`seed-data/regions.json`) cũng không có cấp này. Vì vậy
 * không có ô "Huyện" — thêm vào sẽ là bịa ra một cấp không tồn tại trong dữ liệu.
 *
 * Danh mục 228 dòng nên tải một lần rồi cache cho cả phiên; không cần API lọc
 * theo cha, lọc ngay tại client.
 */
import * as api from '../api-client.js';
import { esc } from './util.js';

let cache = null;
let inflight = null;

/** Tải danh mục địa bàn (dùng chung cho mọi lần mở form trong cùng phiên). */
export async function loadRegions() {
  if (cache) return cache;
  if (!inflight) {
    inflight = api.admin.regions
      .list()
      .then((rows) => {
        cache = Array.isArray(rows) ? rows : (rows?.data ?? []);
        return cache;
      })
      .finally(() => {
        inflight = null;
      });
  }
  return inflight;
}

/**
 * Gắn 2 ô chọn vào `host`.
 *
 * @param {HTMLElement} host
 * @param {{ value?: string|null, required?: boolean }} [opts] - `value` là regionId
 *   cấp xã có sẵn (khi sửa hồ sơ); ô Tỉnh sẽ tự chọn theo cha của nó.
 * @returns {{ getValue: () => string|undefined, setValue: (id: string|null) => void }}
 */
export function createRegionPicker(host, { value = null, required = false } = {}) {
  host.innerHTML = `
    <div class="form-row">
      <label>Tỉnh / Thành${required ? ' *' : ''}</label>
      <select data-role="province" disabled><option value="">Đang tải…</option></select>
    </div>
    <div class="form-row">
      <label>Xã / Phường${required ? ' *' : ''}</label>
      <select data-role="commune" disabled><option value="">— Chọn Tỉnh trước —</option></select>
    </div>`;

  const provinceSel = host.querySelector('[data-role="province"]');
  const communeSel = host.querySelector('[data-role="commune"]');
  let regions = [];

  function communesOf(provinceId) {
    return regions
      .filter((r) => r.level === 'commune' && r.parentId === provinceId)
      .sort((a, b) => a.name.localeCompare(b.name, 'vi'));
  }

  function fillCommunes(provinceId, selected = '') {
    const list = communesOf(provinceId);
    communeSel.innerHTML =
      `<option value="">${provinceId ? '— Chọn Xã / Phường —' : '— Chọn Tỉnh trước —'}</option>` +
      list.map((c) => `<option value="${esc(c.id)}">${esc(c.name)}</option>`).join('');
    communeSel.disabled = !provinceId;
    if (selected) communeSel.value = selected;
  }

  provinceSel.addEventListener('change', () => fillCommunes(provinceSel.value));

  loadRegions()
    .then((rows) => {
      regions = rows;
      const provinces = regions
        .filter((r) => r.level === 'province')
        .sort((a, b) => a.name.localeCompare(b.name, 'vi'));
      provinceSel.innerHTML =
        '<option value="">— Chọn Tỉnh / Thành —</option>' +
        provinces.map((p) => `<option value="${esc(p.id)}">${esc(p.name)}</option>`).join('');
      provinceSel.disabled = false;

      // Có sẵn regionId (cấp xã) thì suy ngược ra tỉnh cha để chọn đúng cả hai ô.
      const current = value ? regions.find((r) => r.id === value) : null;
      if (current) {
        const provinceId = current.level === 'commune' ? current.parentId : current.id;
        provinceSel.value = provinceId ?? '';
        fillCommunes(provinceId, current.level === 'commune' ? current.id : '');
      } else {
        fillCommunes('');
      }
    })
    .catch(() => {
      // Không tải được danh mục thì nói thẳng, đừng để ô chọn rỗng im lặng khiến
      // người dùng tưởng hệ thống chưa có địa bàn nào.
      provinceSel.innerHTML = '<option value="">Không tải được danh mục địa bàn</option>';
      communeSel.innerHTML = '<option value="">—</option>';
    });

  return {
    /** Trả về regionId cấp xã; nếu chỉ chọn tỉnh thì trả về id tỉnh. */
    getValue: () => communeSel.value || provinceSel.value || undefined,
    setValue(id) {
      const r = id ? regions.find((x) => x.id === id) : null;
      if (!r) {
        provinceSel.value = '';
        fillCommunes('');
        return;
      }
      const provinceId = r.level === 'commune' ? r.parentId : r.id;
      provinceSel.value = provinceId ?? '';
      fillCommunes(provinceId, r.level === 'commune' ? r.id : '');
    },
  };
}
